import { css } from '@emotion/react'
import styled from '@emotion/styled'
import DragEventService, { EventPosition } from 'drag-event-service'
import { RefObject, memo, useCallback, useEffect, useRef } from 'react'
import {
    FloaterLocation,
    FloaterModal,
    RoomStateItemX,
} from '../../../../types/mfc'
import {
    generateModalKey,
    getFloaterOrientation,
    getModalState,
    setModalStack,
} from '../../../app-state/modals'
import {
    closeFloater,
    minimizeFloater,
    setFloaterSize,
    updateFloaterLocation,
    updateLocalStorage,
} from '../../../app-state/modals/state.floater'
import { getScreenState } from '../../../app-state/state.screen'
import { FloaterSize } from '../../../constants/enums'
import {
    FLOATING_PREVIEW,
    MY_WEBCAM,
    QUICK_REPLY,
} from '../../../constants/floaters'
import { APPSTATE } from '../../../state/state.app'
import { isTouchDevice } from '../../../utilities/browser'
import {
    TransitionTypes,
    motionTransition,
} from '../../../utilities/transitions'
import { useFloater } from '../../Hooks/useModal'
import CloseIcon from '../../Icons/Close'
import Minus from '../../Icons/Minus'
import Plus from '../../Icons/Plus'
import { palette } from '../../styles/colors'
import layers from '../../styles/layers'
import shape from '../../styles/shape'
import {
    CHAT_HEAD_WIDTH,
    COMPONENT_HEIGHTS,
    PAPER_HEIGHTS,
    getBrowserBottomInset,
} from '../../theme'
import Floaters from './Floaters'
import { PreviewCloseButton, SizeButton } from './FullscreenFloater'

const FloaterRoot = styled('div')({
    position: 'absolute',
    zIndex: PAPER_HEIGHTS.floater - 1,
    opacity: 1,
    transition: 'transform 180ms ease',
    transformStyle: 'preserve-3d',
    display: 'block',
    cursor: 'grab',
    border: palette.layerLightBorder,
})

const movableFloaterStyles = css({
    transition: 'transform 0.3s var(--spring-ease)',
    transform: 'scale(1)',
    '@starting-style': {
        transform: 'scale(0.95)',
    },
    '&:has(:focus-within, :active)': {
        zIndex: PAPER_HEIGHTS.floater,
    },
    '&:active': {
        transform: 'scale(1.01)',
    },
})

const SideButtonContainer = styled('div')({
    position: 'absolute',
    zIndex: PAPER_HEIGHTS.floater,
    display: 'flex',
    flexFlow: 'column nowrap',
})

export const resolveIDs = (id: number, roomState: Partial<RoomStateItemX>) => {
    if (!roomState || !roomState?.id) {
        return { videoID: id, roomID: id }
    }

    const { loungeSurrogateID, id: roomID, isLounge } = roomState || {}
    const videoID = isLounge ? loungeSurrogateID : roomID

    return { loungeSurrogateID, videoID, roomID }
}

type Action = (
    event: MouseEvent | TouchEvent,
    dragStart: boolean,
    metadata: Partial<FloaterModal>,
    data?: any,
) => void

type Check = (
    event: MouseEvent | TouchEvent,
    dragStart: boolean,
    metadata: Partial<FloaterModal>,
) => [checkPassed: boolean, data: any, skipDrag: boolean]

export type FloaterRegisterOptions = {
    abandonDrag?: (event: MouseEvent | TouchEvent) => boolean
    allowDefault?: (event: MouseEvent | TouchEvent) => boolean
    onAction: Action
    onCheck: Check
}

export type FloaterModalComponentProps = {
    vertical: boolean
    isMobile: boolean
    moveFloater: (x: number, y: number) => void
    decreaseSize?: () => void
    increaseSize?: () => void
    eventsRef: RefObject<FloaterRegisterOptions>
    sideButtonsRef?: RefObject<HTMLDivElement>
    show: boolean
    location: FloaterLocation
}
export type LocationRef = {
    originX: number
    originY: number
    pointerX: number
    pointerY: number
    bottomInset: number
}
export const checkParent = (
    target: HTMLElement,
    match: HTMLElement,
    maxDepth = 4,
    depth = 0,
): boolean => {
    if (!match || !target) {
        return false
    }
    if (depth > maxDepth) return false
    if (target === match) return true
    return checkParent(target.parentElement, match, maxDepth, depth + 1)
}

export const clickParent = (target, maxDepth = 4, currentDepth = 0) => {
    if (currentDepth > maxDepth) {
        return false
    }
    if (!target.click)
        return clickParent(target.parentElement, maxDepth, currentDepth + 1)
    else {
        target.click()
        return true
    }
}

const BUTTON_ROOT_LOCATION_MAP = {
    [FloaterSize.Small]: [-28, -28],
    [FloaterSize.Medium]: [-24, -28],
    [FloaterSize.Large]: [-28, -28],
}

const FLOATER_WIDTHS = {
    [QUICK_REPLY]: CHAT_HEAD_WIDTH + 2, // account for border
}

export const floaterMomentum = (
    id: number,
    floaterType: string,
    node: HTMLElement,
    ref: RefObject<number>,
    iteration = 20,
): void => {
    const modalKey = generateModalKey(floaterType, id)
    const floater = getModalState().floater[modalKey]
    const [orientation] = getFloaterOrientation()
    const location = floater.locations[orientation]
    // console.log(
    //     '[floaterMomentum.locations]',
    //     orientation,
    //     location,
    //     floater.state.locations,
    // )
    const canContinue =
        floaterType === QUICK_REPLY && floater.minimized && iteration > 0

    if (!canContinue) return
    const { windowWidth, windowHeight } = getScreenState()
    // the amount to add to the current x position to get to the wall given current direction
    const distanceFromEdgeX =
        location.speedX > 0
            ? // going to the right
              windowWidth - (location.x + FLOATER_WIDTHS[floaterType])
            : // going to the left
              -location.x

    // the amount to add to the current y position to get to the wall given current direction
    // const distanceFromEdgeY =
    //     location.speedY > 0
    //         ? // going to the bottom
    //           WINDOWSIZE.size.height - (location.y + FLOATER_WIDTHS[floaterType])
    //         : // going to the top
    //           -location.y

    const mainDivisor = Math.max(iteration * 0.6, 1)
    const scaledSpeedY = location.speedY * 0.85
    const scaledSpeedX = distanceFromEdgeX / mainDivisor

    const newLocation = {
        x: location.x + scaledSpeedX,
        y: (location.y as number) + scaledSpeedY,
    }
    updateFloaterLocation(id, floaterType, newLocation)
    // console.log(
    //     '[floaterMomentum]',
    //     { mainDivisor, iteration, inverseIteration: 21 - iteration },
    //     { distanceFromEdgeX, scaledSpeedX },
    //     { scaledSpeedY },
    //     location,
    // )
    if (newLocation.x + FLOATER_WIDTHS[floaterType] > windowWidth) {
        // fix the x coord to make sure we aren't out of bounds
        newLocation.x = windowWidth - FLOATER_WIDTHS[floaterType] / 2
        return
    }
    if (newLocation.x < 0) {
        // fix the x coord to make sure we aren't out of bounds
        newLocation.x = newLocation.x - FLOATER_WIDTHS[floaterType] / 2
        return
    }
    if (newLocation.y + FLOATER_WIDTHS[floaterType] > windowHeight - 55) {
        newLocation.y = windowHeight - FLOATER_WIDTHS[floaterType]
        return
    }
    if (newLocation.y < 0) {
        newLocation.y = 0
        return
    }
    // should change this to be transform based instead of setting top/left
    node.style.left = newLocation.x + 'px'
    node.style.top = newLocation.y + 'px'
    ref.current = requestAnimationFrame(() =>
        floaterMomentum(id, floaterType, node, ref, iteration - 1),
    )
}

type FloaterProps = {
    vertical?: boolean
    fullWindow?: boolean
    isMobile?: boolean
    portrait?: boolean
    floaterType: string
    floaterID?: number
}

const getSafeOrientation = (
    isMobile: boolean,
    vertical: boolean,
    fullPage: boolean,
) => {
    if (isMobile) {
        return vertical ? 'portrait' : 'landscape'
    }
    if (fullPage) {
        return vertical ? 'portrait' : 'landscape'
    }
    return 'landscape'
}

const checkEarlyReturn = (floaterType: string, fullWindow: boolean) => {
    if (fullWindow && floaterType === FLOATING_PREVIEW) return true
    return false
}

const Floater = memo((props: FloaterProps) => {
    // const { query } = router
    const container = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const growRef = useRef<HTMLButtonElement>(null)
    const shrinkRef = useRef<HTMLButtonElement>(null)
    const sideButtonsRef = useRef<HTMLDivElement>(null)
    const eventsRef = useRef<FloaterRegisterOptions>(null)
    const location = useRef<LocationRef>({
        originX: 0, // move origin
        originY: 0,
        pointerX: 0, // mouse pointer
        pointerY: 0,
        bottomInset: 0,
    })
    const animationRef = useRef<number>(null)
    const { fullWindow, floaterType, floaterID, isMobile, vertical } = props
    const modalKey = generateModalKey(floaterType, floaterID)
    const [floaterProps, stackPosition] = useFloater(modalKey)

    const {
        show,
        id,
        locations: startingLocations = {},
        stickyHeader = false,
        minimized = true,
        close,
        size = FloaterSize.Small,
    } = floaterProps

    const orientation = getSafeOrientation(
        isMobile || isTouchDevice,
        vertical,
        false,
    )
    // mobile landscape doesn't have the room to have a good experience with quick reply
    const isChatHeadBehavior =
        floaterType === QUICK_REPLY && minimized && vertical

    const shouldLimitMaxX = close || stickyHeader
    const closeLabel =
        floaterType === FLOATING_PREVIEW ? 'Close Preview' : 'Close'
    const growLabel =
        floaterType === FLOATING_PREVIEW ? 'Embiggen Preview' : 'Expand'
    const shrinkLabel =
        floaterType === FLOATING_PREVIEW ? 'Shrink Preview' : 'Shrink'

    const orientedLocation = startingLocations[orientation]

    const decreaseFloaterSize = useCallback(() => {
        if (size > FloaterSize.Small) {
            setFloaterSize(id, floaterType, size - 1)
        }
    }, [size, id])

    const increaseFloaterSize = useCallback(() => {
        if (size < FloaterSize.Fullscreen) {
            setFloaterSize(id, floaterType, size + 1)
        }
    }, [size, id])

    const onDrag = useCallback(
        (event: MouseEvent | TouchEvent, position: EventPosition) => {
            if (
                !eventsRef.current?.allowDefault ||
                !eventsRef.current.allowDefault(event)
            ) {
                event.stopPropagation()
                event.preventDefault()
            }

            if (eventsRef.current?.abandonDrag) {
                const shouldAbandon = eventsRef.current.abandonDrag(event)
                if (shouldAbandon) return
            }

            const diffX = location.current.pointerX - position.pageX
            const diffY = location.current.pointerY - position.pageY
            location.current.pointerX = position.pageX
            location.current.pointerY = position.pageY

            const node = container.current
            if (!node) return

            const left = node.offsetLeft - diffX
            const top = node.offsetTop - diffY
            const height = document.body.clientHeight - node.offsetHeight
            const width = document.body.clientWidth - node.offsetWidth

            // check if we should limit max X or not
            const fixedX = shouldLimitMaxX
                ? Math.min(Math.max(8, left), width - 36)
                : Math.min(Math.max(8, left), width - 8)
            const fixedY = Math.min(
                Math.max(8, top),
                height -
                    (vertical
                        ? 8 +
                          COMPONENT_HEIGHTS.navBottom +
                          location.current.bottomInset
                        : 8),
            )
            cancelAnimationFrame(animationRef.current)
            node.style.left = fixedX + 'px'
            node.style.top = fixedY + 'px'
            node.style.cursor = 'grabbing'
            if (isChatHeadBehavior) {
                node.style.transform = isTouchDevice
                    ? 'scale(1.25)'
                    : 'scale(1.15)'
            }
            // console.log('[Floater->onDrag]', idToSlug[id], { fixedX, fixedY })
            updateFloaterLocation(id, floaterType, {
                x: fixedX,
                y: fixedY,
            })
        },
        [id, floaterType, isChatHeadBehavior, vertical, shouldLimitMaxX],
    )

    const onDragEnd = useCallback(
        (event: MouseEvent | TouchEvent) => {
            if (
                !eventsRef.current?.allowDefault ||
                !eventsRef.current.allowDefault(event)
            ) {
                event.stopPropagation()
                event.preventDefault()
            }

            if (
                Math.abs(
                    location.current.pointerX - location.current.originX,
                ) <= 3 &&
                Math.abs(
                    location.current.pointerY - location.current.originY,
                ) <= 3
            ) {
                const target = event.target as HTMLElement
                //touch/mobile: need to check if the target is one of the buttons (grow, shrink, or close)
                if (checkParent(target, closeRef.current)) {
                    onClose()
                }
                if (checkParent(target, growRef.current)) {
                    increaseFloaterSize()
                }
                if (checkParent(target, shrinkRef.current)) {
                    decreaseFloaterSize()
                }
                if (eventsRef.current?.onCheck && eventsRef.current?.onAction) {
                    const [passed, data] = eventsRef.current.onCheck(
                        event,
                        false,
                        {
                            locations: startingLocations,
                        },
                    )
                    if (passed) {
                        eventsRef.current.onAction(
                            event,
                            false,
                            {
                                locations: startingLocations,
                            },
                            data,
                        )
                    }
                }
                if (isChatHeadBehavior) {
                    motionTransition(
                        () => minimizeFloater(id, floaterType, false),
                        {
                            id,
                            transitionSource: TransitionTypes.FLOATER,
                            transitionDestination: TransitionTypes.FLOATER,
                        },
                    )
                }
            } else if (floaterType === QUICK_REPLY) {
                updateLocalStorage(id)
            }

            const node = container.current
            if (!node) return
            node.style.cursor = 'grab'
            if (isChatHeadBehavior) {
                node.style.transform = 'scale(1)'
                floaterMomentum(id, floaterType, node, animationRef)
            }
            DragEventService.off(document, 'end', onDragEnd)
            DragEventService.off(document, 'move', onDrag)
        },
        [
            id,
            floaterType,
            startingLocations,
            onDrag,
            decreaseFloaterSize,
            increaseFloaterSize,
            isChatHeadBehavior,
        ],
    )

    const onDragStart = useCallback(
        (event: MouseEvent | TouchEvent, position: EventPosition) => {
            // console.log('[Floater->onDrag]', id, floaterType)
            if (
                !eventsRef.current?.allowDefault ||
                !eventsRef.current.allowDefault(event)
            ) {
                event.stopPropagation()
                event.preventDefault()
            }
            if (eventsRef.current?.abandonDrag) {
                const shouldAbandon = eventsRef.current.abandonDrag(event)
                if (shouldAbandon) return
            }
            setModalStack({ modalKey: generateModalKey(floaterType, id) })
            // eslint-disable-next-line fp/no-let
            let shouldSubscribe = true
            if (eventsRef.current?.onCheck && eventsRef.current?.onAction) {
                const [passed, data, skipDrag] = eventsRef.current.onCheck(
                    event,
                    true,
                    {
                        locations: startingLocations,
                    },
                )
                shouldSubscribe = !skipDrag
                // console.log(
                //     '[Floater->onDragStart]',
                //     id,
                //     {
                //         passed,
                //         data,
                //         skipDrag,
                //         shouldSubscribe,
                //     },
                //     event,
                // )

                if (passed) {
                    // event.stopPropagation()
                    // event.preventDefault()
                    eventsRef.current.onAction(
                        event,
                        true,
                        { locations: startingLocations },
                        data,
                    )
                }
            }
            location.current.originX = location.current.pointerX =
                position.pageX
            location.current.originY = location.current.pointerY =
                position.pageY
            location.current.bottomInset = getBrowserBottomInset()

            if (shouldSubscribe) {
                DragEventService.on(document, 'end', onDragEnd, {
                    touchArgs: [{ passive: false }],
                })
                DragEventService.on(document, 'move', onDrag, {
                    touchArgs: [{ passive: false }],
                })
            }
        },
        [id, onDragEnd, onDrag, startingLocations],
    )
    const containerRef = useCallback(
        (ref: HTMLDivElement) => {
            if (ref) {
                container.current = ref
                if (orientedLocation) {
                    container.current.style.left = orientedLocation.x + 'px'
                    if (typeof orientedLocation.y === 'number')
                        container.current.style.top = orientedLocation.y + 'px'
                    else container.current.style.top = orientedLocation.y
                }
                DragEventService.on(ref, 'start', onDragStart, {
                    touchArgs: [{ passive: false }],
                })
            } else {
                if (container.current) {
                    DragEventService.off(
                        container.current,
                        'start',
                        onDragStart,
                    )
                }
            }
        },
        [id, onDragStart, orientedLocation],
    )
    // provide a way for the modal to move itself if needed
    const moveFloater = useCallback(
        (x: number, y: number) => {
            if (container && container.current) {
                const node = container.current
                node.style.left = x + 'px'
                node.style.top = y + 'px'
            }
            updateFloaterLocation(id, floaterType, {
                x,
                y,
            })
        },
        [id, floaterType],
    )

    useEffect(() => {
        if (!id) return
        // we should recheck to make sure we aren't off the screen after an orientation change
        if (floaterType === QUICK_REPLY && !vertical && APPSTATE.app.isMobile) {
            // if we switch to vertical and we're on mobile then we should just minimize it
            minimizeFloater(id, floaterType, true)
        }
        const node = container.current
        if (node && isChatHeadBehavior) {
            node.style.transform = 'scale(1)'
            // console.log('[Floater.useEffect]->floaterMomentum', {
            //     id,
            //     floaterType,
            //     vertical,
            //     isChatHeadBehavior,
            // })
            floaterMomentum(id, floaterType, node, animationRef)
            return () => cancelAnimationFrame(animationRef.current)
        }
    }, [id, floaterType, vertical, isChatHeadBehavior])

    const onClose = useCallback(
        () => closeFloater(id, floaterType),
        [id, floaterType],
    )

    if (
        !floaterType ||
        (floaterType !== MY_WEBCAM && !id) ||
        show === undefined ||
        checkEarlyReturn(floaterType, fullWindow)
    ) {
        // console.log('[Floater] return null', {
        //     fullWindow,
        //     floaterType,
        //     referenceID,
        //     size,
        // })
        return null
    }

    const FloaterContents = Floaters[floaterType]
    // console.log(
    //     '[Floater] rendering',
    //     floaterType,
    //     {
    //         id,
    //         show,
    //         size,
    //         orientation,
    //         minimized,
    //     },
    //     orientedLocation,
    // )
    const [topRoot, rightRoot] = BUTTON_ROOT_LOCATION_MAP[size]

    return (
        <FloaterRoot
            css={movableFloaterStyles}
            ref={containerRef}
            style={{
                zIndex: PAPER_HEIGHTS.quickReply - stackPosition,
                borderRadius: isChatHeadBehavior ? '50%' : shape.borderRadius,
                boxShadow: show && !minimized ? layers.two : 'none',
            }}
            data-testid={`Container__Floater__${floaterType}`}
            id={`floater-${id}`}
        >
            <FloaterContents
                {...floaterProps}
                moveFloater={moveFloater}
                decreaseSize={decreaseFloaterSize}
                increaseSize={increaseFloaterSize}
                vertical={vertical}
                isMobile={isMobile}
                eventsRef={eventsRef}
                sideButtonsRef={sideButtonsRef}
                location={orientedLocation}
            />
            {close && show && (
                <PreviewCloseButton
                    data-testid="PreviewModal__CloseButton"
                    style={{
                        right: rightRoot,
                        top: topRoot,
                    }}
                    title={closeLabel}
                    ref={closeRef}
                >
                    <CloseIcon />
                </PreviewCloseButton>
            )}
            {show && stickyHeader && (
                <SideButtonContainer
                    ref={sideButtonsRef}
                    style={{
                        right: rightRoot,
                        top: topRoot + 38,
                    }}
                >
                    {size < FloaterSize.Large && (
                        <SizeButton
                            data-testid={'PreviewModal__PlusButton'}
                            ref={growRef}
                        >
                            <Plus title={growLabel} size={0.5} />
                        </SizeButton>
                    )}
                    {size > FloaterSize.Small && (
                        <SizeButton
                            data-testid="PreviewModal__MinusButton"
                            ref={shrinkRef}
                        >
                            <Minus title={shrinkLabel} size={0.5} />
                        </SizeButton>
                    )}
                </SideButtonContainer>
            )}
        </FloaterRoot>
    )
})

export default Floater
